import { ActionIcon, Box, Group } from '@mantine/core';
import { IconMenu2, IconMessageCircleStar } from '@tabler/icons-react';
import { useState, type FC } from 'react';
import { useNavigate } from 'react-router';
import { useAiAgentButtonVisibility } from '../../ee/features/aiCopilot/hooks/useAiAgentsButtonVisibility';
import Omnibar from '../../features/omnibar';
import MantineIcon from '../common/MantineIcon';
import MobileNavBarDrawer from './MobileNavBarDrawer';
import { NotificationsMenu } from './NotificationsMenu';

type Props = {
    activeProjectUuid: string | undefined;
    isLoadingActiveProject: boolean;
};

/**
 * DL Fork: mobile NavBar shell:
 *   [☰]  [search trigger ──── ]  [🔔•]  [AI]
 *
 * - Hamburger opens MobileNavBarDrawer (Home + Browse only — see §8.2 for
 *   why Notifications stays in the bell rather than moving into the drawer).
 * - Search trigger is the existing Omnibar full-width.
 * - Notifications bell is the existing <NotificationsMenu> rendered inline.
 * - AI Agents icon (conditional on the visibility hook) navigates to the
 *   same target as the desktop AiAgentsButton.
 */
export const MobileNavBarContent: FC<Props> = ({
    activeProjectUuid,
    isLoadingActiveProject,
}) => {
    const [drawerOpened, setDrawerOpened] = useState(false);
    const navigate = useNavigate();
    const isAiVisible = useAiAgentButtonVisibility();

    return (
        <>
            <Group align="center" wrap="nowrap" gap="xs" w="100%">
                <ActionIcon
                    size="lg"
                    variant="subtle"
                    aria-label="Open menu"
                    onClick={() => setDrawerOpened(true)}
                    style={{ flexShrink: 0 }}
                >
                    <MantineIcon icon={IconMenu2} size="lg" />
                </ActionIcon>

                {!isLoadingActiveProject && activeProjectUuid ? (
                    <Box
                        style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <Omnibar projectUuid={activeProjectUuid} />
                    </Box>
                ) : (
                    <Box style={{ flex: 1, minWidth: 0 }} />
                )}

                {!isLoadingActiveProject && activeProjectUuid ? (
                    <Box style={{ flexShrink: 0 }}>
                        <NotificationsMenu projectUuid={activeProjectUuid} />
                    </Box>
                ) : null}

                {!isLoadingActiveProject && activeProjectUuid && isAiVisible ? (
                    <ActionIcon
                        size="lg"
                        variant="subtle"
                        aria-label="Ask AI"
                        onClick={() =>
                            navigate(`/projects/${activeProjectUuid}/ai-agents`)
                        }
                        style={{ flexShrink: 0 }}
                    >
                        <MantineIcon icon={IconMessageCircleStar} size="lg" />
                    </ActionIcon>
                ) : null}
            </Group>

            <MobileNavBarDrawer
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
                activeProjectUuid={activeProjectUuid}
            />
        </>
    );
};

export default MobileNavBarContent;
